import React, {useCallback, useEffect, useRef} from 'react';

import useAnalyticsBlockQuery, {
  AnalyticsQueryInformation,
} from 'analysis-tool/data-frame-creation/hooks/useAnalyticsBlockQuery';
import {
  getParameterConfigReadShapeFromConfigSpec,
  getParameterConfigsSpecFromReadShape,
} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import DashboardAndBlockSelectorFormGroup, {
  DashboardAndBlockSelectionState,
} from 'insights/analytics-queries/block-selector/DashboardAndBlockSelectorFormGroup';
import ParameterValueFieldList, {ParameterValuesState} from 'insights/parameters/ParameterValueFieldList';
import {AnalyticsParameterConfigSpecRequest} from 'insights/parameters/RESTParameterTypes';
import {
  maybeGetValueFromConfig,
  setValueInConfig,
} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {JSONObject, JSONValue} from 'util/Types';

/**
 * Extracts parameter values from the step config parameters array into a
 * ParameterValuesState (Record<configId, valueJson>) for ParameterValueFieldList.
 */
function parametersToValues(parameters: Array<JSONObject>): ParameterValuesState {
  const values: ParameterValuesState = {};
  for (const param of parameters) {
    const paramConfig = param.config as JSONObject;
    values[String(paramConfig.id)] = param.valueJson;
  }
  return values;
}

function buildAutoPopulatedConfig(
  config: Record<string, JSONValue>,
  blockQueryInfo: AnalyticsQueryInformation
): Record<string, JSONValue> {
  let updatedConfig = setValueInConfig(config, ['parameterizedSQLQuery'], blockQueryInfo.sqlQuery);
  updatedConfig = setValueInConfig(updatedConfig, ['blockName'], blockQueryInfo.name);
  updatedConfig = setValueInConfig(updatedConfig, ['dashboardName'], blockQueryInfo.dashboardName);

  // Persist the full REST parameter config so it can be faithfully reconstructed later, even if the dashboard
  // is edited or deleted.
  const restConfigs = getParameterConfigsSpecFromReadShape(blockQueryInfo.parameterConfigs);
  const newParameters = restConfigs.map((restConfig) => ({
    config: restConfig,
    valueJson: null,
  }));
  updatedConfig = setValueInConfig(updatedConfig, ['parameters'], newParameters);
  return updatedConfig;
}

/**
 * Custom component for selecting a dashboard and query block, and editing parameter values.
 *
 * Auto-populates parameterizedSQLQuery, blockName, dashboardName, and parameters when a block
 * is selected, following the same pattern as CreateDataFrameFromAnalyticsBlockView.
 * Parameters are rendered inline and only shown when the selected block has parameters.
 */
export function DashboardBlockEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const dashboardId = maybeGetValueFromConfig(config, ['dashboardId']) as string | null;
  const blockId = maybeGetValueFromConfig(config, ['blockId']) as string | null;

  const selectionState: DashboardAndBlockSelectionState = {
    analyticsDashboardId: dashboardId,
    analyticsQueryId: blockId,
  };

  // Only fetch block details when auto-populate is needed (parameterizedSQLQuery not yet set).
  // When editing an existing config where fields are already populated, skip the query.
  // handleSelect clears parameterizedSQLQuery when the block changes, re-enabling the fetch.
  const needsAutoPopulate = maybeGetValueFromConfig(config, ['parameterizedSQLQuery']) == null;
  const blockQueryInfo = useAnalyticsBlockQuery({
    analyticsDashboardId: needsAutoPopulate ? dashboardId : null,
    analyticsQueryId: needsAutoPopulate ? blockId : null,
  });

  // Use refs to avoid stale closures in the effect without adding unstable deps
  const configRef = useRef(config);
  configRef.current = config;
  const onChangeConfigRef = useRef(onChangeConfig);
  onChangeConfigRef.current = onChangeConfig;

  // Auto-populate derived fields (parameterizedSQLQuery, blockName, dashboardName, parameters)
  // when the block query data arrives.
  useEffect(() => {
    if (blockQueryInfo != null) {
      const updatedConfig = buildAutoPopulatedConfig(configRef.current, blockQueryInfo);
      onChangeConfigRef.current(
        updatedConfig,
        ['parameterizedSQLQuery'],
        updatedConfig.parameterizedSQLQuery
      );
    }
  }, [blockQueryInfo]);

  const handleSelect = useCallback(
    (newSelectionState: DashboardAndBlockSelectionState) => {
      let updatedConfig = setValueInConfig(config, ['dashboardId'], newSelectionState.analyticsDashboardId);
      if (newSelectionState.analyticsQueryId !== blockId) {
        // Clear derived fields when block changes
        updatedConfig = setValueInConfig(updatedConfig, ['parameterizedSQLQuery'], null);
        updatedConfig = setValueInConfig(updatedConfig, ['parameters'], []);
        updatedConfig = setValueInConfig(updatedConfig, ['blockName'], null);
        updatedConfig = setValueInConfig(updatedConfig, ['dashboardName'], null);
      }
      onChangeConfig(updatedConfig, ['blockId'], newSelectionState.analyticsQueryId);
    },
    [blockId, config, onChangeConfig]
  );

  // Parameters rendering (inline, only shown when block has parameters)
  const parameters = (maybeGetValueFromConfig(config, ['parameters']) as Array<JSONObject>) ?? [];

  const handleSelectParameterValue = useCallback(
    (configId: string, valueJson: JSONValue): void => {
      const currentParams = (maybeGetValueFromConfig(config, ['parameters']) as Array<JSONObject>) ?? [];
      const updatedParameters = currentParams.map((param) => {
        const paramConfig = param.config as JSONObject;
        if (String(paramConfig.id) === configId) {
          return {...param, valueJson};
        }
        return param;
      });
      onChangeConfig(config, ['parameters'], updatedParameters);
    },
    [config, onChangeConfig]
  );

  return (
    <>
      {disabled ? (
        <DashboardAndBlockSelectorFormGroup
          readOnly={true}
          dashboardName={(config.dashboardName as string) ?? ''}
          blockName={(config.blockName as string) ?? ''}
        />
      ) : (
        <DashboardAndBlockSelectorFormGroup
          readOnly={false}
          selectionState={selectionState}
          onSelect={handleSelect}
        />
      )}
      {parameters.length > 0 && (
        <ParameterValueFieldList
          configs={parameters.map((param) =>
            getParameterConfigReadShapeFromConfigSpec(param.config as AnalyticsParameterConfigSpecRequest)
          )}
          selectedValues={parametersToValues(parameters)}
          onSelectValue={handleSelectParameterValue}
          disabled={disabled}
        />
      )}
    </>
  );
}
